import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Gauge, Copy, ChevronDown } from "lucide-react";

function ring(score) {
  if (score >= 75) return "text-signal-green";
  if (score >= 50) return "text-signal-yellow";
  return "text-brand";
}

export default function Scorer({ text, kind = "title", niche = "", testid }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);

  const run = async () => {
    if (!open && !res) {
      setLoading(true);
      try {
        const r = await api.post("/content/score", { text, kind, niche });
        setRes(r.data);
      } catch (e) { toast.error("Scoring failed"); setLoading(false); return; }
      setLoading(false);
    }
    setOpen((o) => !o);
  };

  return (
    <div className="w-full">
      <button data-testid={testid} onClick={run} disabled={loading}
        className="text-[10px] font-mono uppercase tracking-wider text-white/40 hover:text-white flex items-center gap-1 transition-colors">
        <Gauge className="w-3 h-3" /> {loading ? "scoring..." : res ? `score ${res.overall}` : "score"}
        {res && <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>

      {open && res && (
        <div className="mt-2 bg-[#0A0A0A] border border-white/10 rounded p-3 fade-up" data-testid={`${testid}-result`}>
          <div className="flex items-center gap-4 mb-3">
            <div className={`font-mono text-3xl ${ring(res.overall)}`}>{res.overall}<span className="text-sm text-white/30">/100</span></div>
            <p className="text-xs text-white/60 leading-snug flex-1">{res.verdict}</p>
          </div>
          <div className="space-y-1.5 mb-3">
            {(res.metrics || []).map((m) => (
              <div key={m.label} className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-white/40 w-28 shrink-0 truncate">{m.label}</span>
                <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${m.score >= 75 ? "bg-signal-green" : m.score >= 50 ? "bg-signal-yellow" : "bg-brand"}`} style={{ width: `${m.score}%` }} />
                </div>
                <span className="text-[10px] font-mono text-white/60 w-7 text-right">{m.score}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-white/5 pt-2">
            <div className="text-[10px] uppercase tracking-wider text-white/30 font-mono mb-1.5">Stronger versions</div>
            <div className="space-y-1.5">
              {(res.improvements || []).map((s) => (
                <div key={s} className="flex items-start justify-between gap-2 group">
                  <span className="text-xs text-white/70">{s}</span>
                  <button onClick={() => { navigator.clipboard.writeText(s); toast.success("Copied"); }} className="text-white/30 hover:text-white shrink-0 transition-colors"><Copy className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
